import React, { memo, useEffect, useState } from "react";
import { Button, Typography, Dialog, DialogContent } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "@/store";
import {
  setAuth,
  clearTempData,
  setLoading,
  setTempToken,
} from "@/store/slices/authSlice";
import actionsHandler from "@/store/actions/actionHandler";
import OTPInput from "./OTPInput";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import ResendOTP from "./ResendOTP";
import LogoAndInstructions from "./LogoAndInstructions";

const { unAuthenticatedCall: otpVerification } =
  actionsHandler("otpVerification");
const { unAuthenticatedCall: resendOTP } = actionsHandler("sendOTP");

export interface OTPPayload {
  otp: string;
  token: string;
  email: string;
}

interface PhoneVerificationProps {
  successCallback?: (payload: OTPPayload) => void;
  showLoginMessage?: boolean;
  backButton?: boolean;
  onBack?: () => void;
  isDialog?: boolean;
}

const PhoneVerification = ({
  successCallback,
  showLoginMessage = true,
  backButton = false,
  onBack,
  isDialog = false,
}: PhoneVerificationProps) => {
  const { t } = useTranslation("auth");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { tempEmail, tempToken, loading, tempUser } = useSelector(
    (state: RootState) => state.auth,
  );
  const [timer, setTimer] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(false);

  const validationSchema = Yup.object({
    otp: Yup.string()
      .required(t("phoneVerification.form.otp.validation.required"))
      .length(6, t("phoneVerification.form.otp.validation.length")),
  });

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const payload: OTPPayload = {
          otp: values.otp,
          token: tempToken || "",
          email: tempEmail || "",
        };
        if (successCallback) {
          successCallback(payload);
        } else {
          dispatch(setLoading(true));
          const response = await dispatch(
            otpVerification({
              payload: payload,
              method: "post",
            }),
          );

          if (response.ok) {
            dispatch(
              setAuth({
                token: response.data.token,
                user: response.data,
              }),
            );
            dispatch(clearTempData());
            if (showLoginMessage) {
              toast.success(
                response.data.message ||
                  t("phoneVerification.messages.verificationSuccess"),
              );
            }
          }
        }
      } catch (error) {
        toast.error(t("phoneVerification.messages.unexpectedError"));
      } finally {
        dispatch(setLoading(false));
      }
    },
  });

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (timer > 0) {
      setIsResendDisabled(true);
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
      if (interval) {
        clearInterval(interval);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timer]);

  const handleResendOTP = async () => {
    try {
      const response = await dispatch(
        resendOTP({
          payload: { email: tempEmail },
          method: "post",
        }),
      );

      if (response.ok) {
        dispatch(setTempToken(response.data.token));
        setTimer(60);
        toast.success(t("phoneVerification.messages.resendSuccess"));
      }
    } catch (error) {
      console.error(t("phoneVerification.messages.unexpectedError"));
    }
  };

  useEffect(() => {
    if (!tempToken) {
      navigate("/");
    }
  }, [tempToken]);

  return isDialog ? (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      classes={{
        paper: "tw-p-6",
      }}
    >
      <DialogContent className="tw-flex tw-flex-col tw-items-center">
        {backButton && (
          <button
            onClick={onBack}
            className="tw-absolute tw-top-4 tw-left-4 tw-flex tw-items-center tw-gap-2 tw-text-gray-600"
          >
            <img src={BackIcon} alt="Back Icon" className="tw-cursor-pointer" />
            Back
          </button>
        )}

        <div className="tw-w-full tw-max-w-md">
          <LogoAndInstructions tempEmail={tempEmail} />
          {/* OTP Input */}
          <OTPInput formik={formik} loading={loading} />

          {/* Resend Link */}
          <ResendOTP timer={timer} onResend={handleResendOTP} />
        </div>
      </DialogContent>
    </Dialog>
  ) : (
    <div className="tw-min-h-screen tw-flex">
      {/* Left Section */}
      <div className="lg:tw-w-3/4 md:tw-w-2/4 sm:tw-w-full tw-hidden login-bg tw-bg-cover tw-bg-center sm:tw-flex tw-items-end tw-justify-start tw-text-white tw-p-14">
        {backButton && (
          <button
            onClick={onBack}
            className="tw-absolute tw-top-4 tw-left-4 tw-flex tw-items-center tw-gap-2 tw-text-white"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
        )}
        <div className="tw-px-4">
          <h1 className="tw-font-bold font-size-5xl tw-mb-4 tw-leading-[74px]">
            Innovative Collateral & <br /> Risk Solutions
          </h1>
        </div>
      </div>

      {/* Right Section */}
      <div className="lg:tw-w-1/3 md:tw-w-2/4 sm:tw-w-full tw-w-full tw-flex tw-items-center tw-justify-center">
        <div className="tw-w-full tw-max-w-md tw-px-16">
          <LogoAndInstructions tempEmail={tempEmail} />
          {/* OTP Input */}
          <OTPInput formik={formik} loading={loading} />

          {/* Resend Link */}
          <ResendOTP timer={timer} onResend={handleResendOTP} />
        </div>
      </div>
    </div>
  );
};

export default PhoneVerification;
